import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBoxOpen, faChevronLeft, faChevronRight, faCloud, faHistory, faKiwiBird, faRedo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ActivityModal from './tabs/ActivityModal';

export const DOCUMENT_MISSING_REASON = {
  DELETED: 'deleted',
  NEVER_EXISTED: 'never_existed',
  ERROR: 'error',
};

const COPY = {
  [DOCUMENT_MISSING_REASON.DELETED]: {
    title: 'This document was deleted',
    body: 'It is no longer available in MedPrompt. You can still view its audit trail, or go back and continue from where you left off.',
    icon: faBoxOpen,
  },
  [DOCUMENT_MISSING_REASON.NEVER_EXISTED]: {
    title: 'This document does not exist',
    body: 'No document was found for this link. It may have been mistyped, or it never existed in this clinic.',
    icon: faKiwiBird,
  },
  [DOCUMENT_MISSING_REASON.ERROR]: {
    title: "Couldn't load this document",
    body: 'Something went wrong while loading. Check your connection and try again.',
    icon: faCloud,
  },
};

const MissingDocumentView = ({
  reason,
  backLabel,
  onBack,
  navigationContext,
  onNavigatePrev,
  onNavigateNext,
  prevButtonAnimating,
  nextButtonAnimating,
  documentId,
  activeUsers,
  teams,
  showAuditTrail,
  onViewAuditTrail,
  onCloseAuditTrail,
  onRetry,
}) => {
  const copy = COPY[reason] || COPY[DOCUMENT_MISSING_REASON.ERROR];
  const canNavigatePrev = navigationContext && navigationContext.currentIndex > 0;
  const canNavigateNext = navigationContext && navigationContext.currentIndex < navigationContext.documentIds.length - 1;

  return (
    <div className="document-detail-view">
      <div className="document-header">
        <div className="header-left">
          <button className="header-button" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {backLabel}
          </button>
        </div>
        <div className="document-actions">
          {navigationContext && (
            <>
              <button
                className={`header-button nav-button${prevButtonAnimating ? ' nav-button-animating' : ''}`}
                onClick={onNavigatePrev}
                disabled={!canNavigatePrev}
                title={canNavigatePrev ? 'Previous document' : 'No previous document'}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>
              <span className="nav-position">
                {navigationContext.currentIndex + 1} / {navigationContext.documentIds.length}
              </span>
              <button
                className={`header-button nav-button${nextButtonAnimating ? ' nav-button-animating' : ''}`}
                onClick={onNavigateNext}
                disabled={!canNavigateNext}
                title={canNavigateNext ? 'Next document' : 'No next document'}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="missing-document-body">
        <h2 className="missing-document-title">{copy.title}</h2>
        <FontAwesomeIcon icon={copy.icon} className="missing-document-icon" />
        <p>{copy.body}</p>
        <div className="missing-document-actions">
          {reason === DOCUMENT_MISSING_REASON.DELETED && (
            <button className="header-button" onClick={onViewAuditTrail}>
              <FontAwesomeIcon icon={faHistory} /> View audit trail
            </button>
          )}
          {reason === DOCUMENT_MISSING_REASON.ERROR && (
            <button className="header-button header-button-primary" onClick={onRetry}>
              <FontAwesomeIcon icon={faRedo} /> Retry
            </button>
          )}
        </div>
      </div>

      {showAuditTrail && (
        <ActivityModal
          documentId={documentId}
          activeUsers={activeUsers}
          teams={teams}
          onClose={onCloseAuditTrail}
        />
      )}
    </div>
  );
};

export const DocumentLoadingView = () => (
  <div className="document-detail-view">
    <div className="missing-document-body">
      <FontAwesomeIcon icon={faSpinner} spin />
      <p>Loading document...</p>
    </div>
  </div>
);

export default MissingDocumentView;
